import { createSlice, createAsyncThunk, AnyAction, unwrapResult } from '@reduxjs/toolkit';
import { RoleDataArray, RoleModel, RoleQueryParams, RoleItem } from '@app/interfaces/role';
import { createRoleApi, deleteRoleApi, updateRoleApi, getRolesApi, getRoleApi, assignUserRoleApi, assignPermissionRoleApi } from '@app/api/role.api';
import { message } from 'antd';
import { showError } from '@app/utils/message';

export interface RoleState {
  data: RoleModel[];
  totalRows: number;
  pagination: {
    current: number,
    pageSize: number
  },
  filters: any,
  sorter: any,
  searchTerm: string;
  selectedRecord: RoleItem[];
  loading: boolean;
  error: string | null;
  role: RoleModel | null
}

const initialState: RoleState = {
  data: [],
  totalRows: 0,
  pagination: { current: 1, pageSize: 20 },
  filters: {},
  sorter: {},
  searchTerm: "",
  selectedRecord: [],
  loading: false,
  error: null,
  role: null,
};

interface ResponseData {
  data: RoleItem;
  message: string;
}

export const getRoles = createAsyncThunk<RoleDataArray, RoleQueryParams>(
  'role/getRoles',
  async (params: RoleQueryParams, { rejectWithValue }) => {
    try {
      const data = (await getRolesApi(params)) as RoleDataArray;
      return data || [];
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

export const createRole = createAsyncThunk<ResponseData, RoleModel>(
  'role/createRole', async (data) => {
  const response = await createRoleApi(data);
  return response;
});

export const getRole = createAsyncThunk<ResponseData, number>(
  'role/getRole',
  async (roleId, { rejectWithValue }) => {
    try {
      const data = await getRoleApi(roleId);
      return data;

    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'An error occurred'
      );
    }
  }
);

export const updateRole = createAsyncThunk<ResponseData, { roleId: number; formData: RoleModel }>(
  'role/updateRole',
  async ({ roleId, formData }) => {
    const response = await updateRoleApi(roleId, formData);
    return response;
  },
);

export const deleteRole = createAsyncThunk<{ roleId: number; response: any }, number>(
  'role/deleteRole',
  async (roleId, { rejectWithValue }) => {
    try {
      const response = await deleteRoleApi(roleId);
      return { roleId, response };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

export const assignUserRole = createAsyncThunk<ResponseData, { roleId: number; formData: any }>(
  'role/assignUserRole',
  async ({ roleId, formData }) => {
    const response = await assignUserRoleApi(roleId, formData);
    return response;
  },
);

export const assignPermissionRole = createAsyncThunk<ResponseData, { roleId: number; formData: any }>(
  'role/assignPermissionRole',
  async ({ roleId, formData }) => {
    const response = await assignPermissionRoleApi(roleId, formData);
    return response;
  },
);

const handle_payload = (state: any, action: any) => {
  const { data, message: newMessage } = action.payload;
  const roleId = typeof data.id === 'string' ? parseInt(data.id, 10) : data.id;

  const updatedIndex = state.data.findIndex((item: any) => item.id === roleId);
  if (updatedIndex !== -1) {
    state.data[updatedIndex] = data.attributes;
    state.role = data.attributes;
    message.success(newMessage);
  }
}

const isRejectedAction = (action: AnyAction) => {
  return action.type.endsWith('rejected');
};

export const roleSlice = createSlice({
  name: 'role',
  initialState,
  reducers: {
    updateTableParams(state, action) {
      const { pagination, filters, sorter, searchTerm } = action.payload;
      state.pagination = pagination;
      state.filters = filters;
      state.sorter = sorter;
      state.searchTerm = searchTerm;
    }
  },
  extraReducers: (builder) => {
    builder
    .addCase(getRoles.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(getRoles.fulfilled, (state, action) => {
      const { rows, total_rows } = action.payload;

      const data = rows;
      state.data = data.map((item: RoleItem) => item.attributes) || [];

      state.totalRows = parseInt(total_rows, 10);
      state.loading = false;
    })
    .addCase(createRole.fulfilled, (state, action) => {
      const { data, message: newMessage } = action.payload;
      state.data.unshift(data.attributes);
      message.success(newMessage);
    })
    .addCase(getRole.fulfilled, (state, action) => {
      const { data, message: newMessage } = action.payload;

      if (data) {
        state.role = data.attributes;
        message.success(newMessage);
      }
    })
    .addCase(updateRole.fulfilled, (state, action) => {
      handle_payload(state, action)
    })
    .addCase(deleteRole.fulfilled, (state, action) => {
      const { roleId, response } = action.payload;

      if (response.error) { 
        showError(response.error); return;
      }
      else {
        state.data = state.data.filter((order) => order.id !== roleId);
        message.success(response.message);
      }
    })
    .addCase(assignUserRole.fulfilled, (state, action) => {
      handle_payload(state, action)
    })
    .addCase(assignUserRole.rejected, (state, action) => {
      showError(action.payload as string);
    })
    .addCase(assignPermissionRole.fulfilled, (state, action) => {
      handle_payload(state, action)
    })
    .addCase(assignPermissionRole.rejected, (state, action) => {
      showError(action.payload as string);
    })
    .addMatcher(isRejectedAction, (state, action) => {
      state.loading = false;
      showError(action.payload?.message || action.payload?.error || action?.error?.message);
    });
  },
});

export const { updateTableParams } = roleSlice.actions;

export default roleSlice.reducer;
